import { EnumPaymentMethods, type BankWithAccounts, type ProcessCartItem, type ProcessCartRequest } from "@/types";

type UnknownRecord = Record<string, unknown>;

function asNumber(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

type RawBankAccount = {
  id?: unknown;
  Id?: unknown;
  bankId?: unknown;
  BankId?: unknown;
  bankName?: unknown;
  BankName?: unknown;
  accountNumber?: unknown;
  AccountNumber?: unknown;
  accountId?: unknown;
  AccountId?: unknown;
  iban?: unknown;
  Iban?: unknown;
  accountIban?: unknown;
  AccountIban?: unknown;
  accountName?: unknown;
  AccountName?: unknown;
};

type RawBankEntry = {
  id?: unknown;
  Id?: unknown;
  name?: unknown;
  Name?: unknown;
  nameAr?: unknown;
  NameAr?: unknown;
  logo?: unknown;
  Logo?: unknown;
  logoUrl?: unknown;
  LogoUrl?: unknown;
  bank?: UnknownRecord;
  accounts?: RawBankAccount[];
};

export function normalizeBanksWithAccounts(raw: unknown): BankWithAccounts[] {
  const container = (raw ?? {}) as { data?: { banks?: unknown }; banks?: unknown };
  const rawBanks = (container.data?.banks ?? container.banks) as unknown;
  if (!Array.isArray(rawBanks)) return [];

  return rawBanks
    .map((entry): BankWithAccounts | null => {
      const bankEntry = entry as RawBankEntry;
      const bankObj = (bankEntry.bank ?? bankEntry) as UnknownRecord;

      const bankId = asNumber(bankObj.id ?? bankObj.Id ?? bankEntry.id ?? bankEntry.Id);
      if (!bankId) return null;

      const bankName = asString(bankObj.name ?? bankObj.Name ?? bankEntry.name ?? bankEntry.Name);
      const bankNameAr =
        asString(bankObj.nameAr ?? bankObj.NameAr ?? bankEntry.nameAr ?? bankEntry.NameAr) || bankName;
      const bankLogo =
        asString(bankObj.logo ?? bankObj.Logo ?? bankObj.logoUrl ?? bankObj.LogoUrl ?? bankEntry.logo ?? bankEntry.Logo ?? bankEntry.logoUrl ?? bankEntry.LogoUrl) ||
        undefined;

      const accountsRaw = Array.isArray(bankEntry.accounts) ? bankEntry.accounts : [];
      const accounts = accountsRaw
        .map((acc): BankWithAccounts["accounts"][number] | null => {
          const account = acc as RawBankAccount;
          const id = asNumber(account.id ?? account.Id);
          if (!id) return null;

          const accountBankId = asNumber(account.bankId ?? account.BankId) ?? bankId;
          return {
            id,
            bankId: accountBankId,
            bankName: asString(account.bankName ?? account.BankName) || bankName,
            accountNumber:
              asString(account.accountNumber ?? account.AccountNumber ?? account.accountId ?? account.AccountId),
            iban: asString(account.iban ?? account.Iban ?? account.accountIban ?? account.AccountIban),
            accountName: asString(account.accountName ?? account.AccountName) || undefined,
          };
        })
        .filter((value): value is BankWithAccounts["accounts"][number] => value !== null);

      return {
        bank: {
          id: bankId,
          name: bankName,
          nameAr: bankNameAr,
          logo: bankLogo,
        },
        accounts,
      };
    })
    .filter((value): value is BankWithAccounts => value !== null);
}

export function buildBankTransferProcessCartRequest(params: {
  associationId: number;
  items: ProcessCartItem[];
  bankId: number;
  accountId: number;
  imageId: number;
  donorBankText?: string;
  donorBankAccountNum?: string;
  donorMobile?: string;
  donorName?: string;
}): ProcessCartRequest {
  return {
    associationId: params.associationId,
    items: params.items,
    paymentMethod: "bank",
    paymentMethodId: EnumPaymentMethods.BankTransfer,
    bankId: params.bankId,
    accountId: params.accountId,
    imageId: params.imageId,
    donorBankText: params.donorBankText,
    donorBankAccountNum: params.donorBankAccountNum,
    donorMobile: params.donorMobile,
    donorName: params.donorName,
  };
}

